/*
 * BLOOMZ core (encargo A) — loaded once from layout/theme.liquid with `defer`.
 * Defines:
 *   window.bzAdd(variantId, qty)  add to cart + open Horizon's cart drawer already populated
 *   <bz-carousel>                 generic horizontal carousel (CSS scroll-snap + buttons)
 *   <bz-card>                     product card behaviour (photo mini-carousel + quick add by size + wishlist heart)
 *   window.bzWishlist             wishlist without an app: product handles in localStorage ("bz-wishlist")
 * No external libraries.
 */
(() => {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Add to cart — mechanism fixed by BZ-CONTRATO.md (do not change)     */
  /* ------------------------------------------------------------------ */
  async function bzAdd(variantId, qty = 1) {
    const ids = [...document.querySelectorAll('cart-items-component')].map((e) => e.dataset.sectionId).filter(Boolean);
    const r = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: qty }], sections: ids.join(',') }),
    });
    const data = await r.json();
    if (!r.ok) throw new Error(data.description || data.message || 'Add to cart failed');
    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { resource: {}, sourceId: 'bz', data: { source: 'bz', itemCount: qty, variantId: String(variantId), sections: data.sections } } }));
    return data;
  }
  window.bzAdd = bzAdd;

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------ */
  /* Wishlist (no app): list of product handles in localStorage.
   *   bzWishlist.list() / has(handle) / toggle(handle) -> boolean (now saved?)
   *   Every change fires "bz:wishlist:change" on document ({ detail: { handles } }); hearts re-sync from it,
   *   also when another tab changes the list ("storage" event).
   * ------------------------------------------------------------------ */
  const WISH_KEY = 'bz-wishlist';
  let wishMemory = []; // fallback when localStorage is blocked (private mode, disabled storage)
  const bzWishlist = {
    list() {
      try {
        const parsed = JSON.parse(window.localStorage.getItem(WISH_KEY) || '[]');
        return Array.isArray(parsed) ? parsed.filter((item) => typeof item === 'string' && item) : [];
      } catch (error) {
        return wishMemory.slice();
      }
    },
    has(handle) {
      return Boolean(handle) && this.list().includes(handle);
    },
    toggle(handle) {
      if (!handle) return false;
      const handles = this.list();
      const index = handles.indexOf(handle);
      if (index >= 0) handles.splice(index, 1);
      else handles.push(handle);
      wishMemory = handles.slice();
      try {
        window.localStorage.setItem(WISH_KEY, JSON.stringify(handles));
      } catch (error) { /* keep the in-memory list */ }
      document.dispatchEvent(new CustomEvent('bz:wishlist:change', { detail: { handles } }));
      return index < 0;
    },
  };
  window.bzWishlist = bzWishlist;
  window.addEventListener('storage', (event) => {
    if (event.key === null || event.key === WISH_KEY) {
      document.dispatchEvent(new CustomEvent('bz:wishlist:change', { detail: { handles: bzWishlist.list() } }));
    }
  });

  /* ------------------------------------------------------------------ */
  /* <bz-carousel>
   *   [data-track]            scroll-snap track; its direct children are the slides
   *   data-per-view-desktop   slides visible >= 750px  -> CSS var --bz-pv-d
   *   data-per-view-mobile    slides visible <  750px  -> CSS var --bz-pv-m
   *   [data-prev]/[data-next] scroll by one "page"; get `disabled` at the ends
   *   Sets [data-static] on itself when there is nothing to scroll.
   *   Public: refresh()
   * ------------------------------------------------------------------ */
  class BzCarousel extends HTMLElement {
    static get observedAttributes() {
      return ['data-per-view-desktop', 'data-per-view-mobile'];
    }

    connectedCallback() {
      this.track = this.own('[data-track]');
      if (!this.track) return;
      this.prev = this.own('[data-prev]');
      this.next = this.own('[data-next]');
      this.applyPerView();

      this.abort?.abort();
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.schedule = this.schedule.bind(this);

      this.prev?.addEventListener('click', () => this.page(-1), { signal });
      this.next?.addEventListener('click', () => this.page(1), { signal });
      this.track.addEventListener('scroll', this.schedule, { passive: true, signal });
      this.addEventListener('keydown', (event) => this.onKeydown(event), { signal });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.schedule);
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.schedule, { signal });
      }
      this.mutationObserver = new MutationObserver(this.schedule);
      this.mutationObserver.observe(this.track, { childList: true });

      this.update();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.resizeObserver?.disconnect();
      this.mutationObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0;
    }

    attributeChangedCallback() {
      this.applyPerView();
      if (this.track) this.schedule();
    }

    /** First match that belongs to THIS carousel (ignores nested carousels). */
    own(selector) {
      return [...this.querySelectorAll(selector)].find((el) => el.closest('bz-carousel') === this) || null;
    }

    applyPerView() {
      const desktop = parseFloat(this.dataset.perViewDesktop);
      const mobile = parseFloat(this.dataset.perViewMobile);
      if (desktop > 0) this.style.setProperty('--bz-pv-d', String(desktop));
      if (mobile > 0) this.style.setProperty('--bz-pv-m', String(mobile));
    }

    page(direction) {
      if (!this.track) return;
      this.track.scrollBy({ left: direction * this.track.clientWidth, behavior: reducedMotion() ? 'auto' : 'smooth' });
    }

    onKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('bz-carousel') !== this) return;
      if (target.closest('input, select, textarea, [contenteditable="true"], [role="tablist"]')) return;
      event.preventDefault();
      this.page(event.key === 'ArrowRight' ? 1 : -1);
    }

    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.update();
      });
    }

    refresh() {
      this.update();
    }

    update() {
      const track = this.track;
      if (!track) return;
      const max = track.scrollWidth - track.clientWidth;
      const x = Math.abs(track.scrollLeft);
      const scrollable = max > 2;
      this.toggleAttribute('data-static', !scrollable);
      this.setDisabled(this.prev, !scrollable || x <= 2, this.next);
      this.setDisabled(this.next, !scrollable || x >= max - 2, this.prev);
    }

    setDisabled(button, disabled, sibling) {
      if (!button || button.disabled === disabled) return;
      const hadFocus = disabled && document.activeElement === button;
      button.disabled = disabled;
      // keep keyboard users inside the carousel when the focused arrow switches off
      if (hadFocus && sibling && !sibling.disabled) sibling.focus();
    }
  }

  /* ------------------------------------------------------------------ */
  /* <bz-card> — markup in snippets/bz-card.liquid
   *   [data-media-track] + [data-media-prev]/[data-media-next]  photo mini-carousel (wraps around)
   *   [data-bz-add][data-variant-id]                             quick add buttons (one per size, or single "Add")
   *   [data-qa-toggle] / [data-qa-close]                         mobile / touch toggle of the size strip
   *   [data-qa-msg]                                              error message slot
   *   a[data-bz-swatch] + script[data-bz-colors]                 colour dots (2+ colours): switch photo, sizes and links
   *   [data-bz-wish]                                             wishlist heart (aria-pressed) — state in bzWishlist
   * ------------------------------------------------------------------ */
  class BzCard extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.mediaTrack = this.querySelector('[data-media-track]');
      this.busy = false;

      this.addEventListener('click', (event) => this.onClick(event), { signal });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.hasAttribute('data-qa-open')) {
          this.closeQuickAdd();
          this.querySelector('[data-qa-toggle]')?.focus();
        }
      }, { signal });

      // Remaining photos are loading="lazy": warm them up on first sign of interest.
      const warm = () => this.warmImages();
      this.addEventListener('pointerenter', warm, { once: true, signal });
      this.addEventListener('touchstart', warm, { once: true, passive: true, signal });
      this.addEventListener('focusin', warm, { once: true, signal });

      // wishlist heart: restore the saved state and follow changes made from any other card / tab
      this.wishButton = this.querySelector('[data-bz-wish]');
      if (this.wishButton) {
        this.syncWish();
        document.addEventListener('bz:wishlist:change', () => this.syncWish(), { signal });
      }
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.outsideAbort?.abort();
      clearTimeout(this.msgTimer);
    }

    onClick(event) {
      const swatch = event.target instanceof Element ? event.target.closest('a[data-bz-swatch]') : null;
      if (swatch && this.contains(swatch)) {
        // modified clicks (new tab / window) keep the plain link behaviour
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (this.selectColor(swatch)) event.preventDefault();
        return;
      }
      const button = event.target instanceof Element ? event.target.closest('button') : null;
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-bz-wish')) {
        event.preventDefault();
        bzWishlist.toggle(this.dataset.productHandle); // fires bz:wishlist:change -> syncWish() on every card
        return;
      }
      if (button.hasAttribute('data-media-prev')) return this.slide(-1);
      if (button.hasAttribute('data-media-next')) return this.slide(1);
      if (button.hasAttribute('data-qa-toggle')) return this.hasAttribute('data-qa-open') ? this.closeQuickAdd() : this.openQuickAdd();
      if (button.hasAttribute('data-qa-close')) {
        this.closeQuickAdd();
        this.querySelector('[data-qa-toggle]')?.focus();
        return;
      }
      if (button.hasAttribute('data-bz-add')) return this.add(button);
    }

    /* ---- wishlist heart ---- */
    syncWish() {
      if (!this.wishButton) return;
      this.wishButton.setAttribute('aria-pressed', bzWishlist.has(this.dataset.productHandle) ? 'true' : 'false');
    }

    /* ---- photo mini-carousel ---- */
    warmImages() {
      if (this.warmed) return;
      this.warmed = true;
      this.mediaTrack?.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
    }

    slide(direction) {
      const track = this.mediaTrack;
      if (!track) return;
      this.warmImages();
      const count = track.children.length;
      const width = track.clientWidth;
      if (count < 2 || !width) return;
      const index = Math.round(Math.abs(track.scrollLeft) / width);
      const target = (index + direction + count) % count;
      const wrapped = Math.abs(target - index) > 1;
      track.scrollTo({ left: target * width, behavior: wrapped || reducedMotion() ? 'auto' : 'smooth' });
    }

    /* ---- colour swatches (only products with 2+ colours carry the [data-bz-colors] JSON) ---- */
    get colors() {
      if (this.colorData === undefined) {
        this.colorData = null;
        try {
          const node = this.querySelector('script[data-bz-colors]');
          if (node) this.colorData = JSON.parse(node.textContent || 'null')?.colors || null;
        } catch (error) {
          this.colorData = null;
        }
      }
      return this.colorData;
    }

    /** Returns true when the card handled the colour change (so the link must not navigate). */
    selectColor(swatch) {
      const name = swatch.dataset.bzSwatch;
      const color = this.colors?.find((entry) => entry.name === name);
      if (!color) return false;

      this.querySelectorAll('[data-bz-swatch]').forEach((dot) => {
        const active = dot === swatch;
        dot.classList.toggle('bz-card__swatch--active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      if (color.image) this.showImage(color.image);
      this.pointSizes(color);

      // card links -> same product, this colour's variant
      this.querySelectorAll('a.bz-card__link, .bz-card__title a').forEach((link) => {
        try {
          const url = new URL(link.getAttribute('href'), window.location.href);
          url.searchParams.set('variant', String(color.variant));
          link.setAttribute('href', url.pathname + url.search + url.hash);
        } catch (error) { /* keep the original href */ }
      });
      return true;
    }

    /** Scroll the mini-carousel to `src` when it is one of the slides; otherwise swap the first photo. */
    showImage(src) {
      const track = this.mediaTrack;
      if (!track) return;
      const file = (value) => {
        try { return new URL(value, window.location.href).pathname; } catch (error) { return ''; }
      };
      const wanted = file(src);
      const slides = [...track.children];
      let index = slides.findIndex((el) => el instanceof HTMLImageElement && file(el.getAttribute('src') || '') === wanted);
      if (index < 0) {
        const first = slides.find((el) => el instanceof HTMLImageElement);
        if (!first) return;
        const widths = [200, 300, 400, 600, 800, 1000];
        if (/[?&]width=\d+/.test(src)) {
          first.srcset = widths.map((w) => `${src.replace(/([?&]width=)\d+/, `$1${w}`)} ${w}w`).join(', ');
        } else {
          first.removeAttribute('srcset');
        }
        first.src = src;
        index = slides.indexOf(first);
      }
      const target = slides[index];
      if (target instanceof HTMLImageElement) target.loading = 'eager';
      track.scrollTo({ left: index * track.clientWidth, behavior: reducedMotion() ? 'auto' : 'smooth' });
    }

    /** Re-point the quick add buttons to the variants of `color` (variant id + availability per size). */
    pointSizes(color) {
      const setButton = (button, variantId, available, label) => {
        button.disabled = !available;
        if (available) {
          button.setAttribute('data-bz-add', '');
          button.dataset.variantId = String(variantId);
        } else {
          button.removeAttribute('data-bz-add');
          delete button.dataset.variantId;
        }
        button.setAttribute('aria-label', label);
      };
      const sized = this.querySelectorAll('.bz-card__size[data-size]');
      sized.forEach((button) => {
        const size = button.dataset.size;
        const rows = (color.sizes || []).filter((row) => row[0] === size);
        const row = rows.find((entry) => entry[2]) || null;
        setButton(button, row?.[1], Boolean(row), row ? `Add size ${size}, ${color.name} to bag` : `${size}: Sold Out`);
      });
      if (!sized.length) {
        const single = this.querySelector('.bz-card__size--single');
        if (single) setButton(single, color.variant, Boolean(color.available), `Add ${color.name} to bag`);
      }
    }

    /* ---- quick add ---- */
    openQuickAdd() {
      this.setAttribute('data-qa-open', '');
      this.querySelector('[data-qa-toggle]')?.setAttribute('aria-expanded', 'true');
      this.outsideAbort?.abort();
      this.outsideAbort = new AbortController();
      document.addEventListener('click', (event) => {
        if (event.target instanceof Node && !this.contains(event.target)) this.closeQuickAdd();
      }, { signal: this.outsideAbort.signal });
    }

    closeQuickAdd() {
      this.removeAttribute('data-qa-open');
      this.querySelector('[data-qa-toggle]')?.setAttribute('aria-expanded', 'false');
      this.outsideAbort?.abort();
      this.outsideAbort = null;
    }

    async add(button) {
      if (this.busy || button.disabled) return;
      const variantId = button.dataset.variantId;
      if (!variantId) return;
      this.busy = true;
      this.showMessage('');
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
      try {
        await window.bzAdd(variantId, 1);
        this.closeQuickAdd();
      } catch (error) {
        this.showMessage((error && error.message) || 'Add to cart failed');
      } finally {
        this.busy = false;
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
      }
    }

    showMessage(text) {
      const slot = this.querySelector('[data-qa-msg]');
      if (!slot) return;
      clearTimeout(this.msgTimer);
      slot.textContent = text;
      slot.hidden = !text;
      if (text) this.msgTimer = setTimeout(() => this.showMessage(''), 4000);
    }
  }

  customElements.get('bz-carousel') || customElements.define('bz-carousel', BzCarousel);
  customElements.get('bz-card') || customElements.define('bz-card', BzCard);
})();

/* ---------- Red de seguridad de diálogos (cajón de carrito, búsqueda…) ----------
   Horizon fija el <body> (position: fixed) al abrir un diálogo y solo lo libera en closeDialog().
   Su listener de Escape vive en el componente: si el foco cae fuera (p. ej. tras "Remove", que destruye
   el botón enfocado), Escape cierra el <dialog> de forma nativa SIN limpiar → la página queda sin scroll.
   1) Escape nativo (evento `cancel`) → se redirige al cierre de Horizon.
   2) Si aun así un diálogo se cierra dejando el body fijo y no queda ninguno abierto → se restaura. */
(function () {
  if (window.__bzDialogGuard) return;
  window.__bzDialogGuard = true;
  document.addEventListener(
    'cancel',
    function (event) {
      var dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement)) return;
      var host = dialog.closest('dialog-component, cart-drawer-component');
      if (host && typeof host.closeDialog === 'function') {
        event.preventDefault();
        host.closeDialog();
      }
    },
    true
  );
  document.addEventListener(
    'close',
    function (event) {
      if (!(event.target instanceof HTMLDialogElement)) return;
      requestAnimationFrame(function () {
        if (document.querySelector('dialog[open]')) return;
        var body = document.body;
        if (body.style.position === 'fixed') {
          var y = parseInt(body.style.top || '0', 10) * -1;
          body.style.position = '';
          body.style.top = '';
          body.style.width = '';
          window.scrollTo({ top: y, behavior: 'instant' });
        }
        document.documentElement.removeAttribute('scroll-lock');
      });
    },
    true
  );
})();
